import { useQuery } from '@tanstack/react-query';
import { axiosGet } from '../api/axios';

export type MemberProfileResponse = MemberProfile[];

export interface MemberProfile {
  caller_role: CallerRole;
  date_of_birth: string;
  display_name: string;
  eligibility_email: string;
  email_address: string;
  first_name: string;
  gender: string;
  id: number;
  is_partial: boolean;
  is_text_compatible_phone: boolean;
  last_name: string;
  mobile_phone: string;
  pronoun: string;
  race: string;
  status: string;
  test_user: boolean;
  timezone: string;
  user_subscription: any; // TODO: add type if needed
}

interface CallerRole {
  active_subscription: ActiveSubscription;
  created_at: string;
  id: number;
  listener_preferences: ListenerPreferences;
  notes: string;
  onboarding_completed: string;
  package_subscriptions: PackageSubscription[];
  product_credit_subscriptions: any[]; // TODO: add type if needed
  status: string;
  stripe_payment_failed: boolean;
  stripe_payment_method_id: number;
  type: string;
}

interface ActiveSubscription {
  archived_at: string;
  created_at: string;
  crm_archived_at: string;
  end_date: string;
  id: number;
  package: Package;
  package_id: number;
  start_date: string;
}

interface Package {
  client: Client;
  name: string;
}

interface Client {
  name: string;
}

interface ListenerPreferences {
  age_range: AgeRange;
  genders: Genders;
  preferred_language: string;
  languages: string[];
  topic_tags: number[];
  topics: number[];
}

interface AgeRange {
  from: number;
  to: number;
}

interface Genders {
  female: boolean;
  male: boolean;
  other: boolean;
}

interface PackageSubscription {
  archived_at?: string;
  created_at: string;
  crm_archived_at: string;
  end_date: string;
  id: number;
  package: Package2;
  package_id: number;
  start_date: string;
}

interface Package2 {
  client: Client;
  name: string;
}

const getMemberProfile = async (memberId: number): Promise<MemberProfileResponse> => {
  return await axiosGet(`/members/${memberId}/profile`, {}, 'v2').then((memberProfile) => memberProfile.data);
};

export const useMemberProfile = (memberId: number) => {
  const { data, isLoading, error, refetch, isFetching } = useQuery<MemberProfileResponse>(
    ['memberProfile', memberId],
    () => getMemberProfile(memberId),
  );

  const memberProfile = data ? data[0] : undefined;

  return { data: memberProfile, isLoading, error, refetch, isFetching };
};

export type MemberResponse = Member[];

export interface Member {
  caller_role: MemberListCallerRole;
  caller_role_id: number;
  calls_count: number;
  client_id?: number;
  client_name?: string;
  display_name: string;
  first_name: string;
  id: number;
  last_active_at: string;
  last_name: string;
  latest_engagement: string;
  moods_count: number;
  plan_code: string;
  plan_name: string;
  pronoun?: string;
  test_user: boolean;
  user_subscription: any;
}

interface MemberListCallerRole {
  onboarding_completed: string;
}

export enum MemberTypes {
  ALL = 'ALL',
  ACTIVE = 'is_active',
}

export interface MemberPayload {
  page: number;
  limit: number;
  search: string;
  order_by_direction: string;
  order_by: string;
  type: string;
  is_active: boolean | undefined;
}

const getMembers = async (payload: MemberPayload): Promise<MemberResponse> => {
  return await axiosGet(`/members`, payload, 'v2').then((members) => members.data);
};

export const useMembers = (payload: MemberPayload) => {
  const { data, isLoading, error, refetch, isFetching } = useQuery<MemberResponse>(['members', payload], () =>
    getMembers(payload),
  );

  return { data, isLoading, error, refetch, isFetching };
};

export type MemberCountResponse = {
  count: number;
};

const getMemberCount = async (payload: MemberPayload): Promise<MemberCountResponse> => {
  return await axiosGet(`/members/count`, payload, 'v2').then((memberCount) => memberCount.data);
};

export const useMemberCount = (payload: MemberPayload) => {
  const { data, isLoading, error, refetch, isFetching } = useQuery<MemberCountResponse>(['memberCount', payload], () =>
    getMemberCount(payload),
  );

  return { data, isLoading, error, refetch, isFetching };
};

export type MemberLogsResponse = MemberLogs[];
export interface MemberLogs {
  administrator_role_id: any;
  amount: any;
  call_id: any;
  caller_role_id: number;
  client_administrator_role_id: any;
  eligibility_dataset_id: any;
  engagement_id: any;
  event_category: string;
  event_name: string;
  id: number;
  listener_role_id: any;
  message: string;
  request_id: any;
  stripe_payment_intent_id: any;
  stripe_transfer_id: any;
  timestamp: string;
  topic_id: any;
}

interface MemberLogsPayload {
  page: number;
  limit: number;
  search: string;
}

const getMemberLogs = async (memberId: number, payload: MemberLogsPayload): Promise<MemberLogsResponse> => {
  return await axiosGet(`/members/${memberId}/logs`, payload, 'v2').then((memberLogs) => memberLogs.data);
};

export const useMemberLogs = (memberId: number, payload: MemberLogsPayload) => {
  const { data, isLoading, error, refetch, isFetching } = useQuery<MemberLogsResponse>(
    ['memberLogs', memberId, payload],
    () => getMemberLogs(memberId, payload),
  );

  return { data, isLoading, error, refetch, isFetching };
};

export type MemberLogsCountResponse = {
  count: number;
};

const getMemberLogsCount = async (memberId: number, payload: MemberLogsPayload): Promise<MemberLogsCountResponse> => {
  return await axiosGet(`/members/${memberId}/logs/count`, payload, 'v2').then(
    (memberLogsCount) => memberLogsCount.data,
  );
};

export const useMemberLogsCount = (memberId: number, payload: MemberLogsPayload) => {
  const { data, isLoading, error, refetch, isFetching } = useQuery<MemberLogsCountResponse>(
    ['memberLogsCount', memberId, payload],
    () => getMemberLogsCount(memberId, payload),
  );

  return { data, isLoading, error, refetch, isFetching };
};
